import * as React from 'react';

import { cn, RawTooltip, TooltipTrigger } from '@rematter/ui-web';
import { Copy } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';

export type CopyToClipboardProps = {
  text: string;
  icon?: React.ReactNode;
  className?: string;
  copyLabel?: string;
  copiedLabel?: string;
};

export const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
  text,
  icon,
  className,
  copyLabel = 'Copy',
  copiedLabel = 'Copied',
}) => {
  const [showCopied, setShowCopied] = React.useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const onCopy = () => {
    if (text) {
      navigator.clipboard.writeText(text);
      setShowCopied(true);
      setIsTooltipOpen(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setShowCopied(false);
        setIsTooltipOpen(false);
      }, 2000);
    }
  };

  return (
    <div className={cn('@CopyToClipboard flex items-center gap-2', className)}>
      {icon}
      <span className="truncate text-sm">{text}</span>
      <TooltipTrigger
        delay={300}
        isOpen={showCopied ? isTooltipOpen : undefined}
        onOpenChange={(open) => {
          if (!showCopied) setIsTooltipOpen(open);
        }}
      >
        <AriaButton
          onPress={onCopy}
          className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-neutral/60 transition-colors outline-none hover:text-neutral/80"
          aria-label={`Copy ${text} to clipboard`}
        >
          <Copy size={16} />
        </AriaButton>
        <RawTooltip className="bg-neutral text-white">
          {showCopied ? copiedLabel : copyLabel}
        </RawTooltip>
      </TooltipTrigger>
    </div>
  );
};
